//
//  Runs.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Tenant console Runs page: fleet run list with a workstream filter and
//  a live-only toggle, plus the shared RunsTable. Rows poll the live feed.
//  Spend is a transcript-derived estimate and is always labelled est.
//
import { useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk, type Envelope } from '@shared/api/envelope'
import { templateRef, type Page, type Run } from '@shared/api/types'
import type { Session } from '@shared/auth'
import { lagSeconds, useLiveFeed } from '@shared/api/live'
import { href } from '@shared/router'
import { ago, EstSpend, LiveDot } from './format'

export function RunsTable({ items, serverTime = null }: { items: Run[]; serverTime?: number | null }) {
  return (
    <table>
      <thead>
        <tr><th>When</th><th>Workstream</th><th>Agents</th><th className="rt">Deeds</th><th className="rt">Deny/Watch/Hold</th><th className="rt">Spend</th><th></th></tr>
      </thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={7} className="hint">No runs synced yet — runs appear when a session ends and <code>signet sync</code> pushes it.</td></tr>}
        {items.map((r) => (
          <tr key={r.session_id}>
            <td className="hint mono">
              {ago(r.last_at)} <LiveDot live={r.live} lagS={lagSeconds(serverTime, r.last_at)} lagTitle="sync lag: server_time − last_at (§6e) -- how far behind syncd is" />
            </td>
            <td className="mono">{r.workstream_id ?? '—'}</td>
            <td>{r.agents.map((a) => <span key={a.notebook_id} className="pill" style={{ marginRight: 4 }} title={[a.title, a.template ? templateRef(a.template) : null, a.user ? `${a.user.display_name} <${a.user.email}>` : null].filter(Boolean).join(' · ') || undefined}>{a.agent}{a.title ? ` — ${a.title}` : ''}{a.user ? ` (${a.user.display_name})` : ''}</span>)}</td>
            <td className="rt mono">{r.totals.deeds}</td>
            <td className="rt mono">{r.totals.deny}/{r.totals.watch}/{r.totals.hold}</td>
            <td className="rt"><EstSpend usd={r.totals.usd_est} /></td>
            <td style={{ textAlign: 'right' }}><a href={href(`/runs/${r.session_id}`)}>detail ›</a></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default function Runs({ session }: { session: Session }) {
  const [r, setR] = useState<Envelope<Page<Run>> | null>(null)
  const [workstream, setWorkstream] = useState('')
  const [liveOnly, setLiveOnly] = useState(false)

  useEffect(() => {
    setR(null)
    const q = new URLSearchParams()
    if (workstream) q.set('workstream', workstream)
    if (liveOnly) q.set('live', 'true')
    const qs = q.toString()
    api<Page<Run>>(`/fleet/runs${qs ? `?${qs}` : ''}`, {}, session.token).then(setR)
  }, [session.token, workstream, liveOnly])

  const liveFilterQuery = [
    workstream ? `workstream=${encodeURIComponent(workstream)}` : '',
    liveOnly ? 'live=true' : '',
  ].filter(Boolean).join('&')
  const live = useLiveFeed<Run>('/fleet/runs', session.token, liveFilterQuery)
  const items = live.items.length ? live.items : r && isOk(r) ? r.data.items : []

  return (
    <>
      <h2>Runs</h2>
      <div className="row">
        <div>
          <label htmlFor="rf-ws">Workstream</label>
          <input id="rf-ws" value={workstream} onChange={(e) => setWorkstream(e.target.value)} placeholder="workstream id" />
        </div>
        <div className="fixed">
          <label htmlFor="rf-live">
            <input id="rf-live" type="checkbox" style={{ width: 'auto', marginRight: 6 }} checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} />
            Live only
          </label>
        </div>
      </div>
      {r && !isOk(r) && <p className="err" role="alert">{r.error.code}: {r.error.message}</p>}
      {r === null && !live.items.length && <p className="hint">Loading runs…</p>}
      <RunsTable items={liveOnly ? items.filter((i) => i.live) : items} serverTime={live.serverTime} />
    </>
  )
}
